import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { AiSkillPublic } from '@ctrl-enter/types/ai-skills';

type AiSkillState = {
  /** All skills fetched from /ai-skills (UX fields only, no system prompts) */
  availableSkills: AiSkillPublic[];
  /** Per-session active skill — key is sessionId, value is skillId or null */
  selectedSkillIdBySession: Record<string, string | null>;
  /** Last used skill ID — used to pre-seed the selector for new chats */
  lastUsedSkillId: string | null;

  // Actions
  setAvailableSkills: (skills: AiSkillPublic[]) => void;
  selectSkill: (sessionId: string, skillId: string | null) => void;
  clearSkill: (sessionId: string) => void;
  hydrateFromSession: (sessionId: string, aiSkillId: string | null | undefined) => void;
  getSelectedSkill: (sessionId: string) => AiSkillPublic | null;
};

export const useAiSkillStore = create<AiSkillState>()(
  devtools(
    persist(
      (set, get) => ({
        availableSkills: [],
        selectedSkillIdBySession: {},
        lastUsedSkillId: null,

        setAvailableSkills: (skills) => set({ availableSkills: skills }),

        selectSkill: (sessionId, skillId) => {
          set(state => ({
            selectedSkillIdBySession: {
              ...state.selectedSkillIdBySession,
              [sessionId]: skillId,
            },
            lastUsedSkillId: skillId ?? state.lastUsedSkillId,
          }));
        },

        clearSkill: (sessionId) => {
          set(state => ({
            selectedSkillIdBySession: {
              ...state.selectedSkillIdBySession,
              [sessionId]: null,
            },
          }));
        },

        hydrateFromSession: (sessionId, aiSkillId) => {
          set(state => {
            // Only update if not already set in client state (avoid overwriting local changes)
            const current = state.selectedSkillIdBySession[sessionId];
            if (current !== undefined) return {};
            return {
              selectedSkillIdBySession: {
                ...state.selectedSkillIdBySession,
                [sessionId]: aiSkillId ?? null,
              },
            };
          });
        },

        getSelectedSkill: (sessionId) => {
          const { availableSkills, selectedSkillIdBySession } = get();
          const skillId = selectedSkillIdBySession[sessionId];
          if (!skillId) return null;
          return availableSkills.find(s => s.id === skillId) ?? null;
        },
      }),
      {
        name: 'ai-skill-store',
        // Persist per-session skill selections keyed by real session IDs only.
        // Exclude the 'new' sentinel so a page refresh never re-applies a stale
        // specialist to the blank-slate new-chat state.
        // lastUsedSkillId is intentionally NOT persisted — new chats always start
        // unspecialized; per-session state is restored via hydrateFromSession.
        partialize: (state) => ({
          selectedSkillIdBySession: Object.fromEntries(
            Object.entries(state.selectedSkillIdBySession).filter(([key]) => key !== 'new')
          ),
        }),
      }
    ),
    { name: 'ai-skill-store' }
  )
);

// Selector hooks
export const useAvailableSkills = () => useAiSkillStore(s => s.availableSkills);
export const useSelectedSkillId = (sessionId: string) =>
  useAiSkillStore(s => s.selectedSkillIdBySession[sessionId] ?? null);
export const useSelectedSkill = (sessionId: string) =>
  useAiSkillStore(s => s.getSelectedSkill(sessionId));
export const useLastUsedSkillId = () => useAiSkillStore(s => s.lastUsedSkillId);
